import { Alert, Badge, Card, ErrorBanner } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { ChecksList } from './ChecksList';
import { DeclarationNotice } from './DeclarationNotice';
import type { RuntimeStatus } from './Runtime.api';
import { check, keyTri, securityReady, unansweredChecks } from './runtimeVocab';
import s from './Runtime.module.css';

/**
 * Card SecurityReady de T15 (FR-090).
 *
 * O badge é o que o executor concluiu; as oito linhas são o porquê. O alerta
 * final é o resumo em uma frase — e no caso indeterminado ele não promete nada,
 * porque falta leitura, não porque a identidade esteja governada.
 */

/**
 * O aviso do Security Ready indeterminado (aim-ivi.3, decisão 5): com a leitura de pé, ele nomeia os checks que
 * contam e ficaram sem resposta do executor, com os nomes da tela. Com a leitura inteira falha, todos estão sem
 * resposta, e a frase continua a de antes, que diz isso. A vírgula entre os nomes é pontuação, e se compõe aqui.
 */
function undeterminedText(status: RuntimeStatus): string {
  const missing = status.error_code ? [] : unansweredChecks(status.checks, status.checks_not_required ?? []);
  if (missing.length === 0) return t('runtimeStatus.card.undetermined.body');
  return t('runtimeStatus.card.undetermined.missing', {
    checks: missing.map((id) => check(id).rotulo).join(', '),
  });
}

export interface SecurityReadyCardProps {
  status?: RuntimeStatus;
  /**
   * `status` é o **último estado conhecido** (do agregado em cache ou vivo), não
   * o status lido agora desta identidade (04/09). O card o desenha igual — é o
   * que se sabe — e avisa que está buscando enquanto `carregando`.
   */
  stale?: boolean;
  carregando: boolean;
  erro: ApiError | null;
  onRetry: () => void;
}

export function SecurityReadyCard({
  status,
  stale = false,
  carregando,
  erro,
  onRetry,
}: SecurityReadyCardProps) {
  const chave = keyTri(status?.security_ready);
  const badge = securityReady(chave);

  const subtitulo = status
    ? [
        status.namespace && status.workload
          ? `${status.namespace}/${status.workload}`
          : null,
        status.role,
        t('runtimeStatus.page.meta.read', { when: dataHour(status.read_at) ?? '—' }),
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <Card
      title={
        <span className={s.tituloCard}>
          {status?.slug ?? t('runtimeStatus.card.fallbackName')}
          {status ? <Badge tone={badge.tone}>{badge.rotulo}</Badge> : null}
        </span>
      }
      subtitle={subtitulo}
      tip={t('runtimeStatus.card.tip')}
    >
      {erro ? (
        <ErrorBanner
          error={erro}
          title={t('runtimeStatus.card.error')}
          onRetry={onRetry}
        />
      ) : null}

      {carregando && !status ? (
        <p className={s.carregando}>{t('runtimeStatus.card.loading')}</p>
      ) : null}

      {stale && status ? (
        // O «estado que já se conhece» (04/09): a tela não fica em branco
        // esperando o cluster — mostra o que o backend sabia, e diz de quando.
        <p className={s.buscando} role="status" data-testid="status-stale">
          {carregando ? <span className={s.spinner} aria-hidden="true" /> : null}
          {t('runtimeStatus.card.stale', { when: dataHour(status.read_at) ?? '—' })}
          {carregando ? t('runtimeStatus.card.stale.reading') : ''}
        </p>
      ) : null}

      {status ? (
        <>
          {status.spiffe_id ? (
            <p className={s.spiffe} title={status.spiffe_id}>
              {status.spiffe_id}
              {status.spiffe_id_diverges ? (
                <Badge tone="warning">{t('runtimeStatus.card.spiffeDiverges')}</Badge>
              ) : null}
            </p>
          ) : null}

          <ChecksList status={status} />
          {/* aim-ypw: a linha fora da conta, do Postgres, abaixo dos checks que contam. */}
          <DeclarationNotice difference={status.declaration_difference} identityId={status.identity_id} />

          {status.svid_warning ? (
            <Alert tone="warning" title={t('runtimeStatus.card.svidWarning.title')}>
              {t('runtimeStatus.card.svidWarning.body', {
                when: status.svid_expires_at ? ` (${dataHour(status.svid_expires_at)})` : '',
              })}
            </Alert>
          ) : null}

          {status.pods && status.pods.length > 0 ? (
            <ul className={s.pods}>
              {status.pods.map((pod) => (
                <li key={pod.name} className={s.pod}>
                  <span className={s.mono}>{pod.name}</span>
                  <span className={s.podDigest}>{(pod.digests ?? []).join(', ') || '—'}</span>
                  <Badge tone={pod.svid ? 'success' : 'neutral'}>
                    {t(pod.svid ? 'runtimeStatus.card.pod.withSvid' : 'runtimeStatus.card.pod.withoutSvid')}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}

          {chave === 'sim' ? (
            <Alert tone="info" title={t('runtimeStatus.card.ready.title')}>
              {t('runtimeStatus.card.ready.body')}
            </Alert>
          ) : chave === 'nao' ? (
            <Alert tone="danger" title={t('runtimeStatus.card.notReady.title')}>
              {t('runtimeStatus.card.notReady.body')}
            </Alert>
          ) : (
            <Alert tone="warning" title={t('runtimeStatus.card.undetermined.title')}>
              {/* O `<p>` que o `Alert` põe em volta de um texto: com um elemento filho ele não o põe. */}
              <p data-testid="security-ready-undetermined">{undeterminedText(status)}</p>
            </Alert>
          )}
        </>
      ) : null}
    </Card>
  );
}
